// Envíos de proveedor: cada factura/pedido cargado es un envío esperado con su recorrido
// (facturado -> llegado -> rosas verificados -> revisado -> entrada en Factusol). Backend: /api/recepcion-rosas
// Organizado por lo que hay que hacer (almacén): llegados con rosas por verificar, por llegar, pendientes de revisión
// completa y terminados. Una fila por envío con UN botón (el siguiente paso) y el resto en el menú «…».
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import {
  AlertTriangle, CheckCircle2, ChevronDown, ClipboardCheck, ClipboardList, Eye, FileCheck2, FileText, Loader2,
  MoreHorizontal, PackageOpen, RefreshCw, Search, Settings2, Truck, Undo2, Upload, Users, X, type LucideIcon,
} from 'lucide-react'
import {
  Recorrido, SinPermiso, fmtFecha, nombreCorto, useAccesoRecepcion, type Envio, type EstadoEnvio,
} from '@/components/recepcion/comun'

interface ListResponse {
  envios: Envio[]
  estados: { clave: EstadoEnvio; texto: string; n: number }[]
  proveedores: { code: string; name: string }[]
  rosas_disponibles: boolean
  generado_at: string
  usuario: string
  es_admin: boolean
}

interface Ajustes {
  dias_sin_llegar: Record<string, number>
  horas_llegado_sin_verificar: number
  aviso_comercial: { email: string; nombre: string }[]
  aviso_almacen: { email: string; nombre: string }[]
  resumen_minutos: number
  horario_avisos: [number, number]
  url_publica_app: string
}

function AjustesModal({ proveedores, onClose }: { proveedores: { code: string; name: string }[]; onClose: () => void }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['recepcion-ajustes'], queryFn: async () => (await api.get<Ajustes>('/recepcion-rosas/ajustes')).data })
  const [dias, setDias] = useState<Record<string, string>>({})
  const [horas, setHoras] = useState('')
  const [comercial, setComercial] = useState('')
  const [almacen, setAlmacen] = useState('')
  const a = q.data
  const val = (k: string) => dias[k] ?? String(a?.dias_sin_llegar[k] ?? a?.dias_sin_llegar.default ?? '')
  const lista = (s: string, defecto: string) =>
    s.split(',').map((e) => e.trim()).filter(Boolean).map((email) => ({ email, nombre: defecto }))
  const guardar = useMutation({
    mutationFn: async () => {
      const cambios: Record<string, unknown> = {}
      const d: Record<string, number> = { ...(a?.dias_sin_llegar || {}) }
      Object.entries(dias).forEach(([k, v]) => { if (v.trim()) d[k] = parseInt(v, 10) })
      cambios.dias_sin_llegar = d
      if (horas.trim()) cambios.horas_llegado_sin_verificar = parseInt(horas, 10)
      if (comercial.trim()) cambios.aviso_comercial = lista(comercial, 'Comercial JVS')
      if (almacen.trim()) cambios.aviso_almacen = lista(almacen, 'Almacén JVS')
      return api.put('/recepcion-rosas/ajustes', { cambios })
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['recepcion-ajustes'] }); qc.invalidateQueries({ queryKey: ['recepcion-envios'] }); onClose() },
  })
  const input = 'w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white'
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-slate-800 shadow-xl p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Ajustes de avisos</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        {!a ? <Loader2 className="w-6 h-6 animate-spin text-indigo-600" /> : (
          <>
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Días laborables desde que factura hasta avisar «no llega»</p>
              <div className="grid grid-cols-2 gap-2">
                {[...proveedores.filter((p) => p.code), { code: 'default', name: 'Resto de proveedores' }].map((p) => (
                  <label key={p.code} className="text-xs text-slate-500">
                    {p.name}
                    <input className={input} type="number" min={1} max={90} value={val(p.code)} onChange={(e) => setDias({ ...dias, [p.code]: e.target.value })} />
                  </label>
                ))}
              </div>
            </div>
            <label className="block text-xs text-slate-500">
              Horas desde «bulto recibido» para avisar si los rosas siguen sin verificar
              <input className={input} type="number" min={1} placeholder={String(a.horas_llegado_sin_verificar)} value={horas} onChange={(e) => setHoras(e.target.value)} />
            </label>
            <label className="block text-xs text-slate-500">
              Avisos a comercial (correos separados por comas)
              <input className={input} placeholder={a.aviso_comercial.map((d) => d.email).join(', ')} value={comercial} onChange={(e) => setComercial(e.target.value)} />
            </label>
            <label className="block text-xs text-slate-500">
              Avisos a almacén (correos separados por comas)
              <input className={input} placeholder={a.aviso_almacen.map((d) => d.email).join(', ')} value={almacen} onChange={(e) => setAlmacen(e.target.value)} />
            </label>
            <p className="text-xs text-slate-500">Las alertas salen L-V de {a.horario_avisos[0]} a {a.horario_avisos[1]} h, una vez por envío. El resumen a comercial sale al pulsar «Terminar» en la recepción o {a.resumen_minutos} min después de la última marca.</p>
            {guardar.isError && <p className="text-sm text-red-600">{String((guardar.error as any)?.response?.data?.detail || guardar.error)}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600">Cancelar</button>
              <button onClick={() => guardar.mutate()} disabled={guardar.isPending} className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                {guardar.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function AlbaranModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [fichero, setFichero] = useState<File | null>(null)
  const [codigo, setCodigo] = useState('')
  const [nombre, setNombre] = useState('')
  const [documento, setDocumento] = useState('')
  const [pedido, setPedido] = useState('')
  const subir = useMutation({
    mutationFn: async () => {
      const fd = new FormData()
      fd.append('fichero', fichero as File)
      fd.append('supplier_code', codigo)
      fd.append('supplier_name', nombre)
      fd.append('documento', documento)
      if (pedido.trim()) fd.append('factusol_order', pedido.trim())
      return api.post('/recepcion-rosas/albaran', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['recepcion-envios'] }); onClose() },
  })
  const input = 'w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white'
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-slate-800 shadow-xl p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Envío desde albarán (otro proveedor)</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-slate-500">CSV o Excel con cabeceras: <b>Ref proveedor</b> (o <b>Nuestra ref</b>), <b>Descripción</b> y <b>Unidades</b>. ITSYS y Green Cell no hace falta: entran solos con su factura.</p>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs text-slate-500">Código de proveedor (Factusol)<input className={input} value={codigo} onChange={(e) => setCodigo(e.target.value)} /></label>
          <label className="text-xs text-slate-500">Nombre<input className={input} value={nombre} onChange={(e) => setNombre(e.target.value)} /></label>
          <label className="text-xs text-slate-500">Nº de albarán<input className={input} value={documento} onChange={(e) => setDocumento(e.target.value)} /></label>
          <label className="text-xs text-slate-500">Pedido Factusol (opcional)<input className={input} value={pedido} onChange={(e) => setPedido(e.target.value)} placeholder="p. ej. 590" /></label>
        </div>
        <input type="file" accept=".csv,.xlsx,.xlsm,.txt" onChange={(e) => setFichero(e.target.files?.[0] || null)} className="text-sm" />
        {subir.isError && <p className="text-sm text-red-600">{String((subir.error as any)?.response?.data?.detail || subir.error)}</p>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600">Cancelar</button>
          <button onClick={() => subir.mutate()} disabled={!fichero || !codigo || !documento || subir.isPending} className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
            {subir.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear envío'}
          </button>
        </div>
      </div>
    </div>
  )
}

type Seccion = 'verificar' | 'por_llegar' | 'revision' | 'terminados'

const SECCIONES: { clave: Seccion; titulo: string; ayuda: string; icon: LucideIcon; acento: string; contador: string }[] = [
  { clave: 'verificar', titulo: 'Llegados: verificar rosas', ayuda: 'Ya están en el almacén y hay clientes esperando piezas: compruébalas primero.',
    icon: ClipboardCheck, acento: 'text-pink-700 dark:text-pink-300',
    contador: 'bg-gradient-to-br from-pink-500 to-rose-600 text-white border-transparent' },
  { clave: 'por_llegar', titulo: 'Por llegar', ayuda: 'Facturados por el proveedor. En rojo, los que pasan su plazo de llegada.',
    icon: Truck, acento: 'text-sky-700 dark:text-sky-300',
    contador: 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700' },
  { clave: 'revision', titulo: 'Pendiente de revisión completa', ayuda: 'Rosas ya verificados (o sin rosas): queda revisar el resto del pedido.',
    icon: ClipboardList, acento: 'text-indigo-700 dark:text-indigo-300',
    contador: 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700' },
  { clave: 'terminados', titulo: 'Terminados', ayuda: 'Revisados o con entrada en Factusol en los últimos 7 días.',
    icon: CheckCircle2, acento: 'text-emerald-700 dark:text-emerald-300',
    contador: 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700' },
]

const SIETE_DIAS = 7 * 24 * 3600 * 1000

function seccionDe(e: Envio): Seccion {
  if (e.entrada_at || e.revisado_at) return 'terminados'
  if (!e.llegado_at) return 'por_llegar'
  const pendientes = e.rosas.pendientes ?? e.rosas.total - e.rosas.verificadas
  return pendientes > 0 ? 'verificar' : 'revision'
}

const diasDesde = (s?: string | null) => (s ? Math.max(0, Math.floor((Date.now() - new Date(s.slice(0, 10) + 'T00:00:00').getTime()) / 86400000)) : 0)
const haceDias = (n: number) => (n === 0 ? 'hoy' : n === 1 ? 'ayer' : `hace ${n} días`)

// Acciones secundarias de un envío (Hoja PDF, ver recepción, deshacer, entrada en Factusol): menú «…»
function MenuEnvio({ e }: { e: Envio }) {
  const qc = useQueryClient()
  const [abierto, setAbierto] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const boton = useRef<HTMLButtonElement>(null)
  const refrescar = () => { setAbierto(false); qc.invalidateQueries({ queryKey: ['recepcion-envios'] }) }
  const deshacer = useMutation({ mutationFn: async () => api.post(`/recepcion-rosas/envios/${e.id}/deshacer`, { paso: 'llegado' }), onSuccess: refrescar })
  const revisado = useMutation({ mutationFn: async () => api.post(`/recepcion-rosas/envios/${e.id}/revisado`, {}), onSuccess: refrescar })
  const entrada = useMutation({
    mutationFn: async () => api.post(`/recepcion-rosas/envios/${e.id}/entrada-factusol`, { nota: 'marcada en jvs-app' }),
    onSuccess: refrescar,
  })
  useEffect(() => {
    if (!abierto) return
    const fuera = (ev: MouseEvent) => { if (ref.current && !ref.current.contains(ev.target as Node)) setAbierto(false) }
    const esc = (ev: KeyboardEvent) => { if (ev.key === 'Escape') { setAbierto(false); boton.current?.focus() } }
    document.addEventListener('mousedown', fuera)
    document.addEventListener('keydown', esc)
    ref.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
    return () => { document.removeEventListener('mousedown', fuera); document.removeEventListener('keydown', esc) }
  }, [abierto])
  const flechas = (ev: React.KeyboardEvent) => {
    if (ev.key !== 'ArrowDown' && ev.key !== 'ArrowUp') return
    ev.preventDefault()
    const items = Array.from(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') || [])
    const i = items.indexOf(document.activeElement as HTMLElement)
    items[(i + (ev.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length]?.focus()
  }
  const item = 'flex w-full items-center gap-2 px-3 py-2 text-sm text-left text-slate-700 hover:bg-slate-100 focus:bg-slate-100 focus:outline-none dark:text-slate-200 dark:hover:bg-slate-700 dark:focus:bg-slate-700'
  const sec = seccionDe(e)
  return (
    <div ref={ref} className="relative">
      <button ref={boton} type="button" onClick={() => setAbierto((v) => !v)} aria-haspopup="menu" aria-expanded={abierto} title="Más acciones"
        className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100">
        <MoreHorizontal className="w-5 h-5" />
        <span className="sr-only">Más acciones del envío {e.documento}</span>
      </button>
      {abierto && (
        <div role="menu" onKeyDown={flechas} className="absolute right-0 top-full mt-1 z-20 w-64 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-800">
          {e.url_hoja && (
            <a role="menuitem" href={e.url_hoja} target="_blank" rel="noreferrer" className={item} onClick={() => setAbierto(false)}>
              <FileText className="w-4 h-4" /> Hoja de recepción (PDF)
            </a>
          )}
          <Link role="menuitem" to={`/logistics/supplier-shipments/${e.id}/recepcion`} className={item}>
            <Eye className="w-4 h-4" /> Ver recepción y pedido completo
          </Link>
          {sec === 'verificar' && (
            <button role="menuitem" type="button" className={item} onClick={() => revisado.mutate()}>
              <ClipboardList className="w-4 h-4" /> Marcar revisado completo
            </button>
          )}
          {e.llegado_at && !e.entrada_at && (
            <button role="menuitem" type="button" className={item}
              onClick={() => { if (confirm('¿Marcar que administración ya ha dado entrada a este pedido en Factusol? (normalmente se detecta sola)')) entrada.mutate() }}>
              <FileCheck2 className="w-4 h-4" /> Marcar entrada en Factusol
            </button>
          )}
          {e.llegado_at && !e.entrada_at && (
            <button role="menuitem" type="button" className={`${item} text-red-700 dark:text-red-300`}
              onClick={() => { if (confirm('¿Quitar «bulto recibido»? (se quita también lo que depende de ello)')) deshacer.mutate() }}>
              <Undo2 className="w-4 h-4" /> Deshacer «bulto recibido»
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// Una fila por envío: proveedor, pedido y factura, fecha, líneas, clientes que esperan y el siguiente paso
function FilaEnvio({ e }: { e: Envio }) {
  const qc = useQueryClient()
  const llegado = useMutation({
    mutationFn: async () => api.post(`/recepcion-rosas/envios/${e.id}/llegado`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recepcion-envios'] }),
  })
  const revisado = useMutation({
    mutationFn: async () => api.post(`/recepcion-rosas/envios/${e.id}/revisado`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recepcion-envios'] }),
  })
  const sec = seccionDe(e)
  const clientes = e.rosas.clientes_pendientes ?? (e.rosas.total - e.rosas.verificadas > 0 ? e.rosas.pedidos : 0)
  const sinRosas = e.rosas.total === 0
  const dias = e.plazo?.dias ?? diasDesde(e.doc_date || e.facturado_at)
  const fuera = sec === 'por_llegar' && !!e.plazo?.fuera
  const alerta = e.alertas[0]
  const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold whitespace-nowrap shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500 dark:focus-visible:ring-offset-slate-900 disabled:opacity-50'
  return (
    <div data-envio={e.id} data-seccion={sec}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border bg-white dark:bg-slate-800 ${
        sinRosas ? 'border-slate-200/70 dark:border-slate-700/60 opacity-75 hover:opacity-100' : sec === 'verificar' ? 'border-pink-300 dark:border-pink-800' : 'border-slate-200 dark:border-slate-700'
      }`}>
      <Recorrido envio={e} />
      <div className="min-w-0 flex-1 flex items-baseline gap-x-2 overflow-hidden whitespace-nowrap">
        <span className={`font-semibold ${sinRosas ? 'text-slate-600 dark:text-slate-300' : 'text-slate-900 dark:text-white'}`}>{e.supplier_name}</span>
        {e.factusol_order ? <span className="text-sm font-medium text-slate-700 dark:text-slate-200">1-{e.factusol_order}</span> : null}
        <span className="text-xs text-slate-400 dark:text-slate-500 truncate" title={`${e.proveedor === 'albaran' ? 'Albarán' : 'Factura'} ${e.documento}`}>
          {e.proveedor === 'albaran' ? 'albarán ' : ''}{e.documento}
        </span>
      </div>
      <span className={`inline-flex items-center gap-1 text-sm whitespace-nowrap ${fuera ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-slate-500 dark:text-slate-400'}`}
        title={e.plazo ? `Enviado el ${fmtFecha(e.doc_date || e.facturado_at)} · ${e.plazo.laborables} días laborables (plazo de ${e.supplier_name}: ${e.plazo.plazo})${fuera ? ': fuera de plazo' : ''}` : undefined}>
        {fmtFecha(e.doc_date || e.facturado_at).slice(0, 5)} · {haceDias(dias)}
        {alerta && <span title={alerta.texto} role="img" aria-label={alerta.texto}><AlertTriangle className="w-4 h-4 text-amber-500" /></span>}
      </span>
      <span className="hidden md:inline text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap tabular-nums">{e.n_lines} lín · {e.units} uds</span>
      <span className="w-40 shrink-0 flex justify-end">
        {clientes > 0 ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-pink-600 text-white whitespace-nowrap"
            title={`${e.rosas.pendientes ?? e.rosas.total - e.rosas.verificadas} línea(s) rosas sin verificar para ${clientes} pedido(s)`}>
            <Users className="w-3.5 h-3.5" /> {clientes} cliente{clientes === 1 ? '' : 's'} espera{clientes === 1 ? '' : 'n'}
          </span>
        ) : e.rosas.incidencias > 0 ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700 ring-1 ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900 whitespace-nowrap">
            <AlertTriangle className="w-3.5 h-3.5" /> {e.rosas.incidencias} incidencia{e.rosas.incidencias === 1 ? '' : 's'}
          </span>
        ) : e.rosas.total > 0 ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-300 whitespace-nowrap">
            <CheckCircle2 className="w-3.5 h-3.5" /> rosas verificados
          </span>
        ) : null}
      </span>
      <span className="w-44 shrink-0 flex justify-end">
        {sec === 'por_llegar' && (
          <button onClick={() => llegado.mutate()} disabled={llegado.isPending} className={`${btn} bg-sky-600 text-white hover:bg-sky-700`} title="1 clic: guarda tu usuario y la hora">
            {llegado.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageOpen className="w-4 h-4" />} Bulto recibido
          </button>
        )}
        {sec === 'verificar' && (
          <Link to={`/logistics/supplier-shipments/${e.id}/recepcion`} className={`${btn} bg-pink-600 text-white hover:bg-pink-700`}>
            <ClipboardCheck className="w-4 h-4" /> Verificar rosas
          </Link>
        )}
        {sec === 'revision' && (
          <button onClick={() => revisado.mutate()} disabled={revisado.isPending} className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700`}>
            {revisado.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardList className="w-4 h-4" />} Revisado completo
          </button>
        )}
        {sec === 'terminados' && (
          <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
            {e.entrada_at
              ? `Entrada ${fmtFecha(e.entrada_at)}${e.entrada_origen === 'factusol' ? '' : ' · ' + nombreCorto(e.entrada_por_nombre)}`
              : `Revisado ${fmtFecha(e.revisado_at)} · ${nombreCorto(e.revisado_por_nombre)}`}
          </span>
        )}
      </span>
      <MenuEnvio e={e} />
    </div>
  )
}

export default function SupplierShipmentsPage() {
  const acceso = useAccesoRecepcion()
  const qc = useQueryClient()
  const [filtro, setFiltro] = useState<Seccion | ''>('')
  const [proveedor, setProveedor] = useState('')
  const [buscar, setBuscar] = useState('')
  const [verTerminados, setVerTerminados] = useState(false)
  const [ajustes, setAjustes] = useState(false)
  const [albaran, setAlbaran] = useState(false)
  const q = useQuery({
    queryKey: ['recepcion-envios', proveedor],
    queryFn: async () => (await api.get<ListResponse>('/recepcion-rosas/envios', { params: proveedor ? { proveedor } : {} })).data,
    enabled: acceso,
    refetchInterval: 60000,
  })
  const detectar = useMutation({
    mutationFn: async () => api.post('/recepcion-rosas/detectar-entradas'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recepcion-envios'] }),
  })
  const porSeccion = useMemo(() => {
    const t = buscar.trim().toUpperCase()
    const out: Record<Seccion, Envio[]> = { verificar: [], por_llegar: [], revision: [], terminados: [] }
    for (const e of q.data?.envios || []) {
      if (t && ![e.documento, e.supplier_name, String(e.factusol_order || ''), `1-${e.factusol_order || ''}`, e.web_order || '']
        .some((v) => v.toUpperCase().includes(t))) continue
      const sec = seccionDe(e)
      if (sec === 'terminados') {
        const fin = e.entrada_at || e.revisado_at || ''
        if (fin && Date.now() - new Date(fin).getTime() > SIETE_DIAS) continue
      }
      out[sec].push(e)
    }
    // con rosas primero; dentro, el más antiguo primero (lo que más tiempo lleva esperando)
    const orden = (a: Envio, b: Envio) => (a.rosas.total ? 0 : 1) - (b.rosas.total ? 0 : 1) ||
      (a.llegado_at || a.facturado_at).localeCompare(b.llegado_at || b.facturado_at)
    out.verificar.sort(orden); out.por_llegar.sort(orden); out.revision.sort(orden)
    out.terminados.sort((a, b) => (b.entrada_at || b.revisado_at || '').localeCompare(a.entrada_at || a.revisado_at || ''))
    return out
  }, [q.data, buscar])

  if (!acceso) return <SinPermiso />
  const elegir = (s: Seccion) => {
    const nuevo = filtro === s ? '' : s
    setFiltro(nuevo)
    if (nuevo === 'terminados') setVerTerminados(true)
  }
  const visibles = SECCIONES.filter((s) => !filtro || filtro === s.clave)
  const total = SECCIONES.reduce((a, s) => a + porSeccion[s.clave].length, 0)

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="p-2 bg-amber-100 dark:bg-amber-900/40 rounded-lg"><Truck className="w-7 h-7 text-amber-600 dark:text-amber-400" /></span>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Envíos de proveedor</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Lo que han enviado los proveedores, ordenado por lo que hay que hacer. Los pedidos rosas, primero.</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Link to="/reports/pending-parts" className="px-3 py-2 rounded-lg text-sm text-pink-700 hover:bg-pink-50 dark:text-pink-300 dark:hover:bg-pink-900/30">Piezas pendientes</Link>
          <button onClick={() => detectar.mutate()} disabled={detectar.isPending} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800" title="Leer en Factusol qué pedidos ya tienen entrada">
            <RefreshCw className={`w-4 h-4 ${detectar.isPending || q.isFetching ? 'animate-spin' : ''}`} /> Actualizar
          </button>
          <button onClick={() => setAlbaran(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
            <Upload className="w-4 h-4" /> Albarán
          </button>
          {q.data?.es_admin && (
            <button onClick={() => setAjustes(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
              <Settings2 className="w-4 h-4" /> Ajustes
            </button>
          )}
        </div>
      </div>

      {/* Contadores = secciones (clic: ver solo esa) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {SECCIONES.map((s) => {
          const n = porSeccion[s.clave].length
          const on = filtro === s.clave
          const destacado = s.clave === 'verificar'
          return (
            <button key={s.clave} type="button" onClick={() => elegir(s.clave)} aria-pressed={on} data-contador={s.clave}
              className={`flex items-center gap-3 rounded-xl border p-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${s.contador} ${
                on ? 'ring-2 ring-offset-2 ring-indigo-400 dark:ring-offset-slate-900' : ''} ${destacado && n === 0 ? 'opacity-70' : ''}`}>
              <s.icon className={`w-6 h-6 shrink-0 ${destacado ? 'text-white/90' : s.acento}`} />
              <span className="min-w-0">
                <span className={`block text-2xl font-bold leading-none ${destacado ? '' : 'text-slate-900 dark:text-white'}`}>{q.data ? n : '…'}</span>
                <span className={`block text-xs mt-1 truncate ${destacado ? 'text-white/90 font-medium' : 'text-slate-500 dark:text-slate-400'}`}>
                  {s.clave === 'terminados' ? 'Terminados (7 días)' : s.titulo}
                </span>
              </span>
            </button>
          )
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <select value={proveedor} onChange={(e) => setProveedor(e.target.value)} aria-label="Proveedor" className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white">
          <option value="">Todos los proveedores</option>
          {(q.data?.proveedores || []).map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
        </select>
        <div className="relative flex-1 min-w-[14rem] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Buscar factura, pedido (1-583) o pedido web…" aria-label="Buscar envío" className="w-full pl-10 pr-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
        </div>
        {filtro && (
          <button type="button" onClick={() => setFiltro('')} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-sm text-indigo-700 hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-900/30">
            <X className="w-4 h-4" /> Ver todas las secciones
          </button>
        )}
        {q.data && !q.data.rosas_disponibles && <span className="text-xs text-amber-700">Pedidos rosas no disponibles ahora mismo</span>}
      </div>

      {q.isLoading ? (
        <div className="flex flex-col items-center gap-3 py-16"><Loader2 className="w-8 h-8 text-indigo-600 animate-spin" /><p className="text-slate-500">Cargando envíos…</p></div>
      ) : q.isError ? (
        <div className="flex flex-col items-center gap-3 py-16"><AlertTriangle className="w-8 h-8 text-red-500" /><p className="text-red-600">Error al cargar los envíos</p></div>
      ) : total === 0 ? (
        <p className="text-center text-slate-500 py-16">No hay envíos con estos filtros.</p>
      ) : (
        <div className="space-y-6">
          {visibles.map((s) => {
            const lista = porSeccion[s.clave]
            const plegable = s.clave === 'terminados'
            const abierta = !plegable || verTerminados
            return (
              <section key={s.clave} aria-labelledby={`sec-${s.clave}`}>
                <div className="flex items-baseline gap-2 mb-2">
                  {plegable ? (
                    <button type="button" id={`sec-${s.clave}`} data-seccion-toggle={s.clave} onClick={() => setVerTerminados((v) => !v)} aria-expanded={abierta}
                      className={`inline-flex items-center gap-1.5 text-base font-semibold rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${s.acento}`}>
                      <ChevronDown className={`w-4 h-4 transition-transform ${abierta ? '' : '-rotate-90'}`} /> {s.titulo} ({lista.length})
                    </button>
                  ) : (
                    <h2 id={`sec-${s.clave}`} className={`inline-flex items-center gap-1.5 text-base font-semibold ${s.acento}`}>
                      <s.icon className="w-4 h-4" /> {s.titulo} ({lista.length})
                    </h2>
                  )}
                  <span className="text-xs text-slate-500 dark:text-slate-400 truncate">{s.ayuda}</span>
                </div>
                {abierta && (lista.length === 0 ? (
                  <p className="text-sm text-slate-400 italic px-1">Nada en esta sección.</p>
                ) : (
                  <div className="space-y-1.5">{lista.map((e) => <FilaEnvio key={e.id} e={e} />)}</div>
                ))}
              </section>
            )
          })}
        </div>
      )}

      {ajustes && <AjustesModal proveedores={q.data?.proveedores || []} onClose={() => setAjustes(false)} />}
      {albaran && <AlbaranModal onClose={() => setAlbaran(false)} />}
    </div>
  )
}
